/**
 * Offline Indicator Component
 * Shows current network status in the header/navigation
 *
 * Displays:
 * - Green dot + "Online" when connected
 * - Red dot + "Offline" when disconnected
 * - Pending sync count badge
 */

import React from 'react';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { useOffline } from '../../contexts/OfflineContext';
import { cn } from '../../lib/utils';

interface OfflineIndicatorProps {
  className?: string;
  showLabel?: boolean;
  showPendingCount?: boolean;
  compact?: boolean;
}

export function OfflineIndicator({
  className,
  showLabel = true,
  showPendingCount = true,
  compact = false,
}: OfflineIndicatorProps) {
  const {
    isOnline,
    pendingSyncCount,
    failedSyncCount,
    isSyncing,
    triggerSync,
  } = useOffline();

  const totalPending = pendingSyncCount + failedSyncCount;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Network Status */}
      <div
        className={cn(
          'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors',
          isOnline
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        )}
      >
        {isOnline ? (
          <Wifi className="h-3.5 w-3.5" />
        ) : (
          <WifiOff className="h-3.5 w-3.5" />
        )}
        {showLabel && !compact && (
          <span>{isOnline ? 'Online' : 'Offline'}</span>
        )}
      </div>

      {/* Pending Sync Badge */}
      {showPendingCount && totalPending > 0 && (
        <button
          onClick={() => isOnline && triggerSync()}
          disabled={!isOnline || isSyncing}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors',
            failedSyncCount > 0
              ? 'bg-red-100 text-red-700 hover:bg-red-200'
              : 'bg-amber-100 text-amber-700 hover:bg-amber-200',
            (!isOnline || isSyncing) && 'opacity-50 cursor-not-allowed'
          )}
          title={
            isSyncing
              ? 'Syncing...'
              : isOnline
              ? 'Click to sync'
              : 'Waiting for connection'
          }
        >
          {isSyncing ? (
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          ) : failedSyncCount > 0 ? (
            <AlertCircle className="h-3.5 w-3.5" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
          {!compact && (
            <span>
              {totalPending} pending
              {failedSyncCount > 0 && ` (${failedSyncCount} failed)`}
            </span>
          )}
          {compact && <span>{totalPending}</span>}
        </button>
      )}
    </div>
  );
}

/**
 * Simple status dot for minimal UI
 */
export function OfflineStatusDot({ className }: { className?: string }) {
  const { isOnline } = useOffline();

  return (
    <div
      className={cn(
        'h-2 w-2 rounded-full',
        isOnline ? 'bg-green-500' : 'bg-red-500',
        className
      )}
      title={isOnline ? 'Online' : 'Offline'}
    />
  );
}

export default OfflineIndicator;
